import { FileSystemHandler } from './FileSystemHandler.js'
import { MimeTypeDetector } from './MimeTypeDetector.js'
import { XMLBuilder, encodeHrefPath } from './XMLBuilder.js'
import { LockManager } from './LockManager.js'
import { DirectoryListing } from './DirectoryListing.js'
import { FileOperations } from './FileOperations.js'
import { fileEtag, directoryEtag } from './etag.js'
import { checkConditionalHeaders, hasConditionalHeaders } from './conditionals.js'
import { parseIfHeader, submittedTokens, evaluateIfHeader } from './ifHeader.js'
import { DeadProperties } from './deadProperties.js'
import { parsePropfindBody, parseProppatchBody } from './davXml.js'
import { classifyDestination, classifySource, crossServerCopyMove, crossServerPull } from './crossServerTransfer.js'
import { davTrace } from './log.js'
import { createPathLocks } from './pathLock.js'

// Headers that carry credentials and must never be logged verbatim. These logs
// are retrievable remotely via the debug-console-receiver, so a leaked bearer
// token / auth_token cookie here is effectively a credential disclosure.
const SENSITIVE_HEADER_RE = /^(authorization|cookie|set-cookie|proxy-authorization|transferheader[\w-]*|x-[\w-]*(token|key|secret|auth)[\w-]*)$/i

// decodeURIComponent that tolerates literal '%' in names (invalid escape
// sequences return the input unchanged instead of throwing).
function safeDecodeURIComponent(value) {
    try {
        return decodeURIComponent(value)
    } catch {
        return value
    }
}

function lowercaseHeaders(headers) {
    const out = {}
    if (headers && typeof headers === 'object') {
        for (const [k, v] of Object.entries(headers)) out[k.toLowerCase()] = v
    }
    return out
}

// Methods whose target resource participates in conditional evaluation.
const CONDITIONAL_METHODS = new Set(['GET', 'HEAD', 'PUT', 'DELETE', 'MKCOL', 'PROPFIND', 'PROPPATCH', 'COPY', 'MOVE'])

/**
 * Sanitize an X-Forwarded-Prefix header into a safe href prefix. Proxies that
 * strip a client-visible namespace before forwarding (SW group router,
 * discovery peerProxy) always set it; emitted hrefs, Content-Location, and
 * incoming absolute paths (Destination:, If: resource tags) are resolved
 * against it — nephele's request.baseUrl threading, done via a header because
 * the proxy is out-of-process. Anything malformed collapses to '' so a bogus
 * client-supplied value degrades to today's behavior instead of polluting
 * paths.
 */
export function parseForwardedPrefix(value) {
    if (typeof value !== 'string') return ''
    const prefix = value.trim().replace(/\/+$/, '')
    if (!prefix.startsWith('/')) return ''
    if (/[\s<>"'\\]/.test(prefix)) return ''
    if (prefix.split('/').some((seg) => seg === '.' || seg === '..')) return ''
    return prefix
}

/**
 * Derive the client-visible origin ('https://host') from the forwarding
 * headers a proxy sets, for emitting absolute hrefs.
 *
 * Nephele emits absolute hrefs (`<href>https://host/apps/</href>`, verified
 * against the live node server), and mixing forms within one server invites
 * client bugs — so hrefs go absolute whenever the request carries enough
 * information to name an origin.
 *
 * Returns '' when it does not: the direct-peer and service-worker transports
 * have no HTTP hop in front, so there is no origin to name and hrefs stay
 * relative, which RFC 4918 also permits. Anything malformed collapses to ''
 * rather than producing a bogus absolute URI.
 *
 * @param {object} headers
 * @returns {string} 'https://host' or ''
 */
export function parseForwardedOrigin(headers = {}) {
    const host = headers['x-forwarded-host']
    const proto = headers['x-forwarded-proto'] || 'https'
    if (typeof host !== 'string' || !host) return ''
    if (proto !== 'http' && proto !== 'https') return ''
    // A host is authority only — no path, no delimiters, no whitespace.
    if (/[\s<>"'\\/?#@]/.test(host)) return ''
    return `${proto}://${host}`
}

// Methods gated by locks on the request-URI resource. COPY reads its source;
// its destination is gated separately in the COPY/MOVE dispatch.
const WRITE_METHODS = new Set(['PUT', 'DELETE', 'MKCOL', 'PROPPATCH', 'MOVE'])

// Methods that run under the per-path write lock (pathLock.js): everything
// that changes a resource's version, so a precondition checked at the top
// of the handler still holds when the write lands. COPY reads its source
// under the lock and holds its destination.
const LOCKED_METHODS = new Set(['PUT', 'DELETE', 'MKCOL', 'PROPPATCH', 'MOVE', 'COPY'])

/**
 * The client-visible prefix an instance's paths carry: the proxy-stripped
 * namespace plus the instance's route prefix. Destination: paths and If:
 * resource tags arrive in that namespace and are localized by stripping it.
 */
function clientRoutePrefix(forwardedPrefix, requestPath, remainingPath) {
    return forwardedPrefix + (requestPath.endsWith(remainingPath)
        ? requestPath.slice(0, requestPath.length - remainingPath.length)
        : '')
}

/** A local Destination's pathname as an engine path. */
function localizeDestinationPath(pathname, routePrefix) {
    let destPath = pathname
    if (routePrefix && destPath.startsWith(routePrefix)) {
        destPath = destPath.slice(routePrefix.length)
    }
    if (destPath.length > 1 && destPath.endsWith('/')) destPath = destPath.slice(0, -1)
    return destPath
}

// DAV: live properties PROPPATCH must not modify.
const PROTECTED_PROPS = new Set([
    'getetag',
    'getcontentlength',
    'getlastmodified',
    'getcontenttype',
    'resourcetype',
    'creationdate',
    'lockdiscovery',
    'supportedlock',
    'displayname',
])

// Return a shallow copy of a proxied request safe to console.log: sensitive
// headers are masked and the (possibly large/binary/sensitive) body is summarized.
// Exported so app layers (main.js) can log requests without leaking
// Authorization / TransferHeaderAuthorization to the remote debug console.
export function sanitizeRequestForLog(request) {
    if (!request || typeof request !== 'object') return request
    const safe = { ...request }
    if (request.headers && typeof request.headers === 'object') {
        const masked = {}
        for (const [k, v] of Object.entries(request.headers)) {
            masked[k] = SENSITIVE_HEADER_RE.test(k) ? '«redacted»' : v
        }
        safe.headers = masked
    }
    if (safe.body != null) {
        const b = safe.body
        const len =
            typeof b === 'string' ? b.length
            : b.byteLength ?? b.size ?? undefined
        safe.body = `«body omitted${len != null ? `: ${len} bytes` : ''}»`
    }
    return safe
}

export class WebDAVFileSystem {
    constructor() {
        this.fileSystemHandler = new FileSystemHandler()
        // Per-path write lock; named by the root directory so a second
        // context over the same store (the service worker's local mount,
        // another tab) shares it.
        this.pathLocks = createPathLocks({ scope: () => this.fileSystemHandler.rootHandle?.name ?? '' })
        this.mimeTypeDetector = new MimeTypeDetector()
        this.xmlBuilder = new XMLBuilder()
        this.lockManager = new LockManager()
        this.deadProperties = new DeadProperties()
        this.directoryListing = new DirectoryListing(this.mimeTypeDetector)
        this.fileOperations = new FileOperations(
            this.fileSystemHandler,
            this.mimeTypeDetector,
            this.directoryListing,
            this.lockManager
        )
    }

    get rootHandle() {
        return this.fileSystemHandler.rootHandle
    }

    ready() {
        return this.fileSystemHandler.ready()
    }

    async selectDirectory() {
        return await this.fileSystemHandler.selectDirectory()
    }

    detectMimeType(filename) {
        return this.mimeTypeDetector.detectMimeType(filename)
    }

    async getFileHandle(path, options = {}) {
        return await this.fileSystemHandler.getFileHandle(path, options)
    }

    async LOCK(path, body, headers = {}, submitted = new Set()) {
        if (!this.fileSystemHandler.rootHandle) {
            throw { status: 503, message: 'No directory selected' }
        }
        await this.lockManager.ensureLoaded()

        try {
            let resourceExists = true
            try {
                await this.getFileHandle(path)
            } catch (error) {
                if (error.name === 'NotFoundError') {
                    resourceExists = false
                } else {
                    throw error
                }
            }

            // LOCK on an unmapped URL creates a locked empty resource
            // (RFC 4918 §7.3 — the modern replacement for lock-null).
            // Parent must exist, like any create.
            if (!resourceExists) {
                const parentPath = path.split('/').slice(0, -1).join('/')
                try {
                    await this.getFileHandle(parentPath, { getDirectory: true })
                } catch {
                    throw { status: 409, message: `Parent collection does not exist: ${path}` }
                }
            }

            const lockResult = this.lockManager.createLock(path, body, headers, submitted)

            if (!resourceExists && !lockResult.refreshed) {
                try {
                    await this.getFileHandle(path, { create: true })
                } catch (error) {
                    this.lockManager.removeLocksForPath(path)
                    throw { status: 500, message: `Could not create locked empty resource: ${error.message}` }
                }
            }

            return this.xmlBuilder.buildLockResponse(
                lockResult.token,
                lockResult.lock,
                resourceExists
            )
        } catch (error) {
            if (error.status) {
                throw error
            } else {
                throw {
                    status: 500,
                    message: error.message || 'Internal server error',
                }
            }
        }
    }

    async UNLOCK(path, headers = {}) {
        if (!this.fileSystemHandler.rootHandle) {
            throw { status: 503, message: 'No directory selected' }
        }
        await this.lockManager.ensureLoaded()

        try {
            this.lockManager.removeLock(path, headers)
            return {
                status: 204,
                headers: {},
                body: '',
            }
        } catch (error) {
            console.warn('UNLOCK: ', error)
            throw error
        }
    }

    async GET(path, options = {}) {
        return await this.fileOperations.GET(path, options)
    }

    async PUT(path, content, mimeType, headers = {}) {
        return await this.fileOperations.PUT(path, content, mimeType, headers)
    }

    async MKCOL(path, headers = {}) {
        return await this.fileOperations.MKCOL(path, headers)
    }

    async DELETE(path, headers = {}) {
        return await this.fileOperations.DELETE(path, headers)
    }

    async getDirectoryListingJSON(path, dirHandle, entryFilter = null) {
        return await this.directoryListing.getDirectoryListingJSON(
            path,
            dirHandle,
            this.lockManager,
            entryFilter
        )
    }

    async getDirectoryListingJSONDetailed(path, dirHandle, entryFilter = null) {
        return await this.directoryListing.getDirectoryListingJSONDetailed(
            path,
            dirHandle,
            this.xmlBuilder,
            entryFilter
        )
    }

    async buildPropResponse(handle, path, selection = { mode: 'allprop' }, hrefPrefix = '') {
        return await this.xmlBuilder.buildPropResponse(
            handle,
            path,
            this.mimeTypeDetector,
            this.lockManager.getLocks(),
            selection,
            await this.deadProperties.get(path),
            hrefPrefix
        )
    }

    buildMultistatusXML(responses) {
        return this.xmlBuilder.buildMultistatusXML(responses)
    }

    escapeXML(str) {
        return this.xmlBuilder.escapeXML(str)
    }

    /**
     * Resolve the target's validators for conditional evaluation:
     * { exists, etag?, lastModified? }. Directory ETags come from the
     * listing snapshot so 304-polling a write-box sees new entries.
     */
    async _resolveValidators(path) {
        try {
            const handle = await this.getFileHandle(path)
            if (handle.kind === 'file') {
                const file = await handle.getFile()
                return { exists: true, etag: await fileEtag(file, path), lastModified: file.lastModified }
            }
            return { exists: true, etag: await directoryEtag(handle), lastModified: null }
        } catch {
            return { exists: false }
        }
    }

    // Cache for slow PROPFIND results: key → { result, timestamp }
    _propfindCache = new Map()
    _PROPFIND_CACHE_TTL = 5 * 60 * 1000 // 5 minutes
    _PROPFIND_CACHE_THRESHOLD = 20000    // only cache if request took >20s
    _propfindInflight = new Map()        // dedup concurrent requests

    // Depth: infinity is real recursion since Phase 3, bounded to keep one
    // request from walking an entire disk; overflow answers 507 per the
    // parity plan.
    static MAX_PROPFIND_ENTRIES = 5000

    async PROPFIND(fileSystemPath, depth = 0, webdavBasePath = null, entryFilter = null, selection = { mode: 'allprop' }, hrefPrefix = '') {
        if (!this.fileSystemHandler.rootHandle)
            throw { status: 503, message: 'No directory selected' }

        const selectionKey =
            selection.mode === 'prop'
                ? `prop:${selection.props.map((p) => `${p.xmlns}|${p.name}`).join(',')}`
                : selection.mode
        // hrefPrefix and webdavBasePath are baked into the cached XML's hrefs,
        // so both must key the cache — the same share reached via a different
        // route or proxy prefix must not serve another namespace's hrefs.
        // The entry filter is per caller (scope, dotfile visibility): its key
        // too, or the dedup and the slow-result cache hand one caller's view
        // of a directory to another (found 2026-10-06).
        const cacheKey = `${hrefPrefix}|${webdavBasePath ?? ''}|${fileSystemPath}:${depth}:${selectionKey}|${entryFilter ? (entryFilter.cacheKey ?? 'filtered') : ''}`

        // Check cache
        const cached = this._propfindCache.get(cacheKey)
        if (cached && (Date.now() - cached.timestamp) < this._PROPFIND_CACHE_TTL) {
            davTrace(`[PROPFIND] CACHE HIT ${fileSystemPath} depth=${depth} (age ${((Date.now() - cached.timestamp) / 1000).toFixed(0)}s)`)
            return cached.result
        }

        // Dedup in-flight requests
        if (this._propfindInflight.has(cacheKey)) {
            davTrace(`[PROPFIND] DEDUP ${fileSystemPath} depth=${depth} — waiting for in-flight request`)
            return this._propfindInflight.get(cacheKey)
        }

        const promise = this._propfindUncached(fileSystemPath, depth, webdavBasePath, entryFilter, cacheKey, selection, hrefPrefix)
        this._propfindInflight.set(cacheKey, promise)
        try {
            return await promise
        } finally {
            this._propfindInflight.delete(cacheKey)
        }
    }

    /**
     * Collect { entry, path } pairs for the target and its members down to
     * `depth`, throwing 507 past MAX_PROPFIND_ENTRIES.
     */
    async _collectPropfindEntries(handle, basePath, depth, entryFilter, out) {
        out.push({ entry: handle, childWebDAVPath: basePath })
        if (out.length > WebDAVFileSystem.MAX_PROPFIND_ENTRIES) {
            throw {
                status: 507,
                message: `PROPFIND exceeds ${WebDAVFileSystem.MAX_PROPFIND_ENTRIES} entries — narrow the target or reduce Depth`,
            }
        }
        if (depth > 0 && handle.kind === 'directory') {
            for await (const entry of handle.values()) {
                let childWebDAVPath = basePath
                if (!childWebDAVPath.endsWith('/')) childWebDAVPath += '/'
                childWebDAVPath += entry.name

                // The entry filter: true shown, false hidden, a status
                // refused — listed with that status, never descended. (A
                // dotfile the caller may not read is refused, not hidden.)
                const verdict = entryFilter ? await entryFilter(entry.name, childWebDAVPath, entry.kind) : true
                if (verdict === false) continue
                if (verdict !== true) {
                    out.push({ entry, childWebDAVPath, refused: verdict })
                    continue
                }

                await this._collectPropfindEntries(entry, childWebDAVPath, depth - 1, entryFilter, out)
            }
        }
    }

    async _propfindUncached(fileSystemPath, depth, webdavBasePath, entryFilter, cacheKey, selection = { mode: 'allprop' }, hrefPrefix = '') {
        const t0 = performance.now()
        davTrace(`[PROPFIND] START ${fileSystemPath} depth=${depth}`)

        try {
            // Resolve file-or-directory: forcing { getDirectory: true } here
            // made depth-0 PROPFIND on a file throw TypeMismatchError → 500
            // (found by the conformance suite, browserdav-nephele-parity).
            const handle = await this.getFileHandle(fileSystemPath)
            davTrace(`[PROPFIND] ${fileSystemPath} got handle +${(performance.now() - t0).toFixed(0)}ms`)

            // handleRequest decoded the request path at entry, so basePath
            // and FS child names are uniformly raw; XMLBuilder encodes the
            // whole href once at emission.
            const basePath = webdavBasePath || fileSystemPath
            const collected = []
            await this._collectPropfindEntries(handle, basePath, depth, entryFilter, collected)
            davTrace(`[PROPFIND] ${fileSystemPath} enumerated ${collected.length} entries +${(performance.now() - t0).toFixed(0)}ms`)

            // Process in parallel batches of 50 to avoid overwhelming the browser
            const responses = []
            const BATCH_SIZE = 50
            for (let i = 0; i < collected.length; i += BATCH_SIZE) {
                const batch = collected.slice(i, i + BATCH_SIZE)
                const batchResults = await Promise.all(
                    batch.map(({ entry, childWebDAVPath, refused }) =>
                        refused
                            ? this.xmlBuilder.buildStatusResponse(entry, childWebDAVPath, refused, hrefPrefix)
                            : this.buildPropResponse(entry, childWebDAVPath, selection, hrefPrefix)
                    )
                )
                responses.push(...batchResults)
                davTrace(`[PROPFIND] ${fileSystemPath} props ${Math.min(i + BATCH_SIZE, collected.length)}/${collected.length} +${(performance.now() - t0).toFixed(0)}ms`)
            }

            davTrace(`[PROPFIND] ${fileSystemPath} building XML for ${responses.length} responses +${(performance.now() - t0).toFixed(0)}ms`)
            const xml = this.buildMultistatusXML(responses)
            const elapsed = performance.now() - t0
            davTrace(`[PROPFIND] ${fileSystemPath} DONE ${responses.length} entries, ${xml.length} bytes, ${elapsed.toFixed(0)}ms total`)

            const result = { xml, contentType: 'application/xml; charset=utf-8' }

            // Cache slow results
            if (elapsed > this._PROPFIND_CACHE_THRESHOLD) {
                davTrace(`[PROPFIND] ${fileSystemPath} CACHED (took ${(elapsed / 1000).toFixed(1)}s, TTL 5min)`)
                this._propfindCache.set(cacheKey, { result, timestamp: Date.now() })
            }

            return result
        } catch (error) {
            // A path that isn't there is an answer (404), not a failure: a
            // client polling a directory that doesn't exist yet (GeoCamera's
            // history, today's shot folders) logged an error every time
            if (error.name === 'NotFoundError') {
                davTrace(`[PROPFIND] ${fileSystemPath} not found +${(performance.now() - t0).toFixed(0)}ms`)
                throw { status: 404, message: `Resource not found: ${fileSystemPath}` }
            }
            console.error(`[PROPFIND] ${fileSystemPath} FAILED +${(performance.now() - t0).toFixed(0)}ms`, error)
            throw {
                status: error.status || 500,
                message: error.message || 'Internal server error',
            }
        }
    }

    // Allow lists only what handleRequest actually dispatches (POST returns
    // 405). Accept-Ranges: bytes is advertised on GET/HEAD responses —
    // Range/206 landed in FileOperations via rangeRequests.js.
    static IMPLEMENTED_METHODS = 'OPTIONS,GET,HEAD,PUT,DELETE,PROPFIND,PROPPATCH,MKCOL,COPY,MOVE,LOCK,UNLOCK'

    async OPTIONS(path) {
        return {
            status: 200,
            headers: {
                DAV: '1,2',
                Allow: WebDAVFileSystem.IMPLEMENTED_METHODS,
                'Content-Type': 'text/plain',
            },
            body: '',
        }
    }

    /**
     * Write methods run under a per-path lock held from the precondition
     * check through the write, so "If-Match, then write" is one step per
     * resource (conditional-writes-are-not-atomic, 2026-10-31): of several
     * writers that read the same version, one lands and the rest get 412.
     * COPY and MOVE hold their destination too, taken in sorted order so
     * two crossing transfers cannot wait on each other. A pull-mode COPY
     * answers 202 and streams while the pull runs, so a lock would release
     * before its bytes land; it is not gated here.
     */
    async handleRequest(request) {
        const paths = this._lockPathsFor(request)
        if (!paths.length) return this._handleRequest(request)
        return this.pathLocks.withLocks(paths, () => this._handleRequest(request))
    }

    _lockPathsFor(request) {
        const method = request.method
        if (!LOCKED_METHODS.has(method)) return []
        const remainingPath = safeDecodeURIComponent(request.remainingPath)
        if (method !== 'COPY' && method !== 'MOVE') return [remainingPath]
        const reqHeaders = lowercaseHeaders(request.headers)
        if (method === 'COPY' && reqHeaders.source) return []
        const requestPath = safeDecodeURIComponent(request.path || request.remainingPath)
        const forwardedPrefix = parseForwardedPrefix(reqHeaders['x-forwarded-prefix'])
        const routePrefix = clientRoutePrefix(forwardedPrefix, requestPath, remainingPath)
        const destination = classifyDestination({
            destHeader: reqHeaders.destination,
            routePrefix,
            selfHosts: request.crossServer?.selfHosts || [],
        })
        if (destination.kind !== 'local') return [remainingPath]
        return [remainingPath, localizeDestinationPath(destination.pathname, routePrefix)]
    }

    async _handleRequest(request) {
        davTrace('HANDLE REQUEST: ', sanitizeRequestForLog(request))
        const { method, remainingPath: rawRemainingPath, query, requestId, body, headers, entryFilter } =
            request

        // Decode the request path ONCE at entry, like nephele does — every
        // method below sees raw filesystem names. Previously only PROPFIND
        // decoded (locally), so PUT/GET/DELETE/COPY operated on
        // percent-encoded names: `PUT a%20b.txt` stored a file literally
        // named "a%20b.txt" while PROPFIND listed "a b.txt" entries.
        const remainingPath = safeDecodeURIComponent(rawRemainingPath)
        const requestPath = safeDecodeURIComponent(request.path || rawRemainingPath)

        if (!this.fileSystemHandler.rootHandle && method !== 'OPTIONS') {
            return { status: 503, body: 'No directory selected', requestId }
        }

        await this.lockManager.ensureLoaded()

        // Conditional requests, mirroring Nephele's ordering: resolve the
        // target's validators, evaluate preconditions, only then dispatch.
        const reqHeaders = lowercaseHeaders(headers)
        const forwardedPrefix = parseForwardedPrefix(reqHeaders['x-forwarded-prefix'])
        // Emitted hrefs go absolute to match nephele; the relative
        // forwardedPrefix stays the namespace for *interpreting* what clients
        // send us — If: resource tags and Destination: — so this addition
        // cannot change how any incoming path is resolved.
        const hrefPrefix = parseForwardedOrigin(reqHeaders) + forwardedPrefix
        if (CONDITIONAL_METHODS.has(method) && hasConditionalHeaders(reqHeaders)) {
            const validators = await this._resolveValidators(remainingPath)
            const failure = checkConditionalHeaders(method, reqHeaders, validators)
            if (failure) return { ...failure, requestId }
        }

        // RFC 4918 If header: precondition (412 when no list holds) and
        // lock-token submission for the write gate below.
        let submittedLockTokens = new Set()
        if (reqHeaders.if !== undefined) {
            let parsedIf
            try {
                parsedIf = parseIfHeader(reqHeaders.if)
            } catch (error) {
                return { status: 400, body: `Malformed If header: ${error.message}`, requestId }
            }
            submittedLockTokens = submittedTokens(parsedIf)
            // If: resource tags arrive in the client-visible namespace.
            const routePrefix = clientRoutePrefix(forwardedPrefix, requestPath, remainingPath)
            const holds = await evaluateIfHeader(parsedIf, async (resourceTag) => {
                let targetPath = remainingPath
                if (resourceTag) {
                    let pathname = resourceTag
                    try {
                        if (/^https?:\/\//i.test(resourceTag)) pathname = new URL(resourceTag).pathname
                    } catch {
                        return null
                    }
                    pathname = decodeURIComponent(pathname)
                    if (routePrefix && pathname.startsWith(routePrefix)) {
                        targetPath = pathname.slice(routePrefix.length)
                    } else if (pathname.startsWith('/')) {
                        targetPath = pathname
                    } else {
                        return null
                    }
                }
                const v = await this._resolveValidators(targetPath)
                return {
                    etag: v.exists ? v.etag : null,
                    lockTokens: this.lockManager.tokensCovering(targetPath),
                }
            })
            if (!holds) {
                return { status: 412, body: 'Precondition Failed: If header', requestId }
            }
        }

        // Lock gate for write methods (COPY's destination is gated in its
        // dispatch case, where the Destination header is parsed).
        if (WRITE_METHODS.has(method)) {
            const lockFailure = this.lockManager.checkWritable(remainingPath, submittedLockTokens)
            if (lockFailure) return { ...lockFailure, requestId }
        }

        const path = remainingPath

        try {
            switch (method) {
                case 'OPTIONS':
                    try {
                        const result = await this.OPTIONS(path)
                        return {
                            status: result.status,
                            headers: result.headers,
                            body: result.body,
                            requestId,
                        }
                    } catch (error) {
                        return {
                            status: error.status || 500,
                            body: error.message || 'Internal server error',
                            requestId,
                        }
                    }

                case 'GET':
                case 'HEAD':
                    // HEAD is GET sharing the same status and header set,
                    // with the body dropped (and Range ignored).
                    try {
                        const content = await this.GET(path, {
                            requestHeaders: reqHeaders,
                            entryFilter,
                            method,
                        })
                        const responseHeaders = {
                            'Content-Type': content.mimeType,
                            'Accept-Ranges': 'bytes',
                            ...content.headers,
                        }
                        if (responseHeaders['X-Acequia-Directory-JSON']) {
                            // Nephele advertises the canonical directory URL
                            // (trailing slash) via Content-Location — in the
                            // client-visible namespace.
                            const fullPath = forwardedPrefix + requestPath
                            responseHeaders['Content-Location'] = fullPath.endsWith('/')
                                ? fullPath
                                : fullPath + '/'
                        }
                        return {
                            status: content.status || 200,
                            body: method === 'HEAD' ? '' : content.file || content.json,
                            headers: responseHeaders,
                            requestId,
                        }
                    } catch (error) {
                        return {
                            status: error.status || 500,
                            body: error.message || 'Internal server error',
                            // 416 carries Content-Range: bytes */size
                            ...(error.headers ? { headers: error.headers } : {}),
                            requestId,
                        }
                    }

                case 'PUT':
                    try {
                        // Streamed transport: pipe the live body stream to
                        // disk instead of materializing it (request.bodyStream
                        // is set for routes registered with streaming: true).
                        // A file store wants the transport's bytes. The group's
                        // request normalization also hands the handler a decoded
                        // `body` — for application/json a parsed OBJECT — which the
                        // buffered (same-browser, postMessage) path used to write and
                        // 415 on; the relay path streams and never saw it. rawBody is
                        // the canonical ArrayBuffer whenever normalization ran.
                        const result = await this.PUT(path, request.bodyStream || request.rawBody || body, undefined, headers)
                        return result.status === 201
                            ? { status: 201, body: 'Resource created', requestId }
                            : { status: 204, body: '', requestId }
                    } catch (error) {
                        return {
                            status: error.status || 500,
                            body: error.message || 'Internal server error',
                            requestId,
                        }
                    }

                case 'PROPFIND':
                    try {
                        let depth = 1
                        if (headers?.depth !== undefined) {
                            if (headers.depth === 'infinity') {
                                depth = Infinity
                            } else {
                                // parseInt('0') is 0, and `0 || 1` is 1 — so the
                                // old `|| 1` silently promoted Depth: 0 to depth 1,
                                // returning a full listing where the client asked
                                // for the resource itself only. Guard on NaN, not
                                // falsiness, so 0 survives.
                                const parsed = parseInt(headers.depth, 10)
                                depth = Number.isNaN(parsed) ? 1 : parsed
                            }
                        }

                        const fileSystemPath = remainingPath
                        const fullWebDAVPath = requestPath
                        const selection = parsePropfindBody(typeof body === 'string' ? body : null)

                        const propfindResult = await this.PROPFIND(
                            fileSystemPath,
                            depth,
                            fullWebDAVPath,
                            entryFilter,
                            selection,
                            hrefPrefix
                        )
                        return {
                            status: 207,
                            body: propfindResult.xml,
                            headers: {
                                'Content-Type': propfindResult.contentType,
                            },
                            requestId,
                        }
                    } catch (error) {
                        return {
                            status: error.status || 500,
                            body: error.message || 'Internal server error',
                            requestId,
                        }
                    }

                case 'MKCOL':
                    try {
                        await this.MKCOL(path, headers)
                        return {
                            status: 201,
                            body: 'Collection created',
                            requestId,
                        }
                    } catch (error) {
                        return {
                            status: error.status || 500,
                            body: error.message || 'Internal server error',
                            requestId,
                        }
                    }

                case 'DELETE':
                    try {
                        await this.DELETE(path, headers)
                        await this.deadProperties.invalidateTree(path)
                        return {
                            status: 204,
                            body: '',
                            requestId,
                        }
                    } catch (error) {
                        return {
                            status: error.status || 500,
                            body: error.message || 'Internal server error',
                            requestId,
                        }
                    }

                case 'COPY':
                case 'MOVE':
                    try {
                        // Destination: paths arrive in the client-visible
                        // namespace and are localized by stripping it.
                        const routePrefix = clientRoutePrefix(forwardedPrefix, requestPath, remainingPath)
                        const overwrite = String(reqHeaders.overwrite || 'T').toUpperCase() !== 'F'

                        // Pull mode (HTTP-TPC): a COPY to THIS path naming its
                        // Source. The request path is the destination; the
                        // caller's grant must let it write there. Mirrors
                        // localWebDAV's crossServerPull.mjs (2026-10-17).
                        if (method === 'COPY' && reqHeaders.source) {
                            if (reqHeaders.destination) {
                                return { status: 400, body: 'A COPY carries Source or Destination, not both', requestId }
                            }
                            if (!request.crossServer) {
                                return { status: 502, body: 'Cross-server transfer is not configured for this origin', requestId }
                            }
                            const src = await classifySource(reqHeaders.source, {
                                allowInsecure: request.crossServer.allowInsecure === true,
                                checkOutbound: request.crossServer.checkOutbound || null,
                            })
                            if (!src.ok) return { status: 400, body: src.reason, requestId }
                            const destExists = await this.fileOperations.exists(remainingPath)
                            if (request.authorizeTransfer) {
                                const verdict = await request.authorizeTransfer({ method: 'PULL', destPath: remainingPath, destExists })
                                if (!verdict?.allowed) {
                                    return { status: 403, body: verdict?.reason || 'Forbidden', requestId }
                                }
                            }
                            const gate = this.lockManager.checkWritable(remainingPath, submittedLockTokens)
                            if (gate) return { ...gate, requestId }
                            const guard = (u) => classifySource(u, {
                                allowInsecure: request.crossServer.allowInsecure === true,
                                checkOutbound: request.crossServer.checkOutbound || null,
                            })
                            const result = await crossServerPull({
                                srcUrl: src.url,
                                destPath: remainingPath,
                                headers: reqHeaders,
                                fileOperations: this.fileOperations,
                                overwrite,
                                destExists,
                                guard,
                                markerIntervalMs: request.crossServer.markerIntervalMs,
                                maxResumes: request.crossServer.maxResumes,
                                resumeBackoffMs: request.crossServer.resumeBackoffMs,
                                // The 202 report streams while the pull runs; the
                                // dead properties are invalidated when it lands.
                                onDone: async (ok) => { if (ok) await this.deadProperties.invalidateTree(remainingPath) },
                            })
                            return { status: result.status, headers: result.headers, body: result.body, bodyFlush: result.bodyFlush, requestId }
                        }

                        const destination = classifyDestination({
                            destHeader: reqHeaders.destination,
                            routePrefix,
                            selfHosts: request.crossServer?.selfHosts || [],
                        })
                        if (destination.kind === 'invalid') {
                            return { status: 400, body: destination.reason, requestId }
                        }

                        if (destination.kind === 'remote') {
                            // Third-party copy (TPC): push to the destination
                            // server with the TransferHeader* convention.
                            // Requires app-provided crossServer context —
                            // embedders that never configured it fail loud
                            // rather than mis-localizing a foreign URL.
                            if (!request.crossServer) {
                                return {
                                    status: 502,
                                    body: 'Cross-server transfer is not configured for this origin',
                                    requestId,
                                }
                            }
                            const result = await crossServerCopyMove({
                                method,
                                srcPath: remainingPath,
                                destUrl: destination.url,
                                headers: reqHeaders,
                                fileSystemHandler: this.fileSystemHandler,
                                overwrite,
                            })
                            if (result.sourceDeleted) {
                                this.lockManager.removeLocksForPath(remainingPath)
                                await this.deadProperties.invalidateTree(remainingPath)
                            }
                            return {
                                status: result.status,
                                headers: result.headers,
                                body: result.body,
                                requestId,
                            }
                        }

                        // The same localization _lockPathsFor used, so the
                        // lock taken above is on the path written below.
                        const destPath = localizeDestinationPath(destination.pathname, routePrefix)

                        const depth = reqHeaders.depth === '0' ? 0 : Infinity

                        // Two-leg authorization. The app's gate authorized the
                        // *request* path only; a transfer also binds (and, on
                        // overwrite, unbinds) at the destination. Nephele asks
                        // its adapter's isAuthorized for the destination — here
                        // the app hands down an authorizer with the same
                        // decomposition (copy-and-move-under-grant-triples
                        // issue C, app side). Absent (embedders, conformance
                        // adapter) the protocol core stays unguarded, as before.
                        if (request.authorizeTransfer) {
                            const destExists = await this.fileOperations.exists(destPath)
                            const verdict = await request.authorizeTransfer({
                                method,
                                sourcePath: remainingPath,
                                destPath,
                                destExists,
                            })
                            if (!verdict?.allowed) {
                                return { status: 403, body: verdict?.reason || 'Forbidden', requestId }
                            }
                        }

                        const destGate = this.lockManager.checkWritable(destPath, submittedLockTokens)
                        if (destGate) return { ...destGate, requestId }

                        const result =
                            method === 'COPY'
                                ? await this.fileOperations.COPY(remainingPath, destPath, { overwrite, depth })
                                : await this.fileOperations.MOVE(remainingPath, destPath, { overwrite })

                        await this.deadProperties.invalidateTree(destPath)
                        if (method === 'MOVE') {
                            await this.deadProperties.moveTree(remainingPath, destPath)
                            this.lockManager.removeLocksForPath(remainingPath)
                        } else if (depth > 0) {
                            await this.deadProperties.copyTree(remainingPath, destPath)
                        }

                        return {
                            status: result.status,
                            body: result.status === 201 ? 'Created' : '',
                            requestId,
                        }
                    } catch (error) {
                        return {
                            status: error.status || 500,
                            body: error.message || 'Internal server error',
                            requestId,
                        }
                    }

                case 'PROPPATCH':
                    try {
                        try {
                            await this.getFileHandle(remainingPath)
                        } catch {
                            return { status: 404, body: `Resource not found: ${remainingPath}`, requestId }
                        }

                        const updates = parseProppatchBody(typeof body === 'string' ? body : null)

                        // All-or-nothing (RFC 4918 §9.2): protected DAV:
                        // properties fail with 403; if anything fails, every
                        // other update reports 424 and nothing is applied.
                        const results = []
                        let hasFailure = false
                        for (const set of updates.sets) {
                            const isProtected = set.xmlns === 'DAV:' && PROTECTED_PROPS.has(set.name)
                            results.push({ prop: set, action: 'set', status: isProtected ? 403 : 200 })
                            if (isProtected) hasFailure = true
                        }
                        for (const remove of updates.removes) {
                            const isProtected = remove.xmlns === 'DAV:' && PROTECTED_PROPS.has(remove.name)
                            results.push({ prop: remove, action: 'remove', status: isProtected ? 403 : 200 })
                            if (isProtected) hasFailure = true
                        }

                        if (!hasFailure) {
                            const toSet = {}
                            for (const set of updates.sets) {
                                toSet[this.deadProperties.propKey(set.xmlns, set.name)] = {
                                    xmlns: set.xmlns,
                                    name: set.name,
                                    value: set.value,
                                }
                            }
                            if (Object.keys(toSet).length > 0) {
                                await this.deadProperties.set(remainingPath, toSet)
                            }
                            const toRemove = updates.removes.map((r) =>
                                this.deadProperties.propKey(r.xmlns, r.name)
                            )
                            if (toRemove.length > 0) {
                                await this.deadProperties.removeProps(remainingPath, toRemove)
                            }
                        }

                        const statusLine = (code) =>
                            code === 200
                                ? 'HTTP/1.1 200 OK'
                                : code === 403
                                  ? 'HTTP/1.1 403 Forbidden'
                                  : 'HTTP/1.1 424 Failed Dependency'
                        let nsCounter = 0
                        const propstats = results
                            .map((r) => {
                                const finalStatus = r.status === 200 && hasFailure ? 424 : r.status
                                const prefix = `ZZ${++nsCounter}`
                                const element =
                                    r.prop.xmlns === 'DAV:'
                                        ? `<D:${r.prop.name}/>`
                                        : `<${prefix}:${r.prop.name} xmlns:${prefix}="${this.escapeXML(r.prop.xmlns)}"/>`
                                return `
        <D:propstat>
            <D:prop>${element}</D:prop>
            <D:status>${statusLine(finalStatus)}</D:status>${
                finalStatus === 403
                    ? `
            <D:error><D:cannot-modify-protected-property/></D:error>`
                    : ''
            }
        </D:propstat>`
                            })
                            .join('')

                        const responseXml = `<?xml version="1.0" encoding="utf-8"?>
<D:multistatus xmlns:D="DAV:">
    <D:response>
        <D:href>${this.escapeXML(encodeHrefPath(hrefPrefix + requestPath))}</D:href>${propstats}
    </D:response>
</D:multistatus>`

                        return {
                            status: 207,
                            headers: { 'Content-Type': 'application/xml; charset=utf-8' },
                            body: responseXml,
                            requestId,
                        }
                    } catch (error) {
                        return {
                            status: error.status || 500,
                            body: error.message || 'Internal server error',
                            requestId,
                        }
                    }

                case 'LOCK':
                    try {
                        const lockResult = await this.LOCK(path, body, reqHeaders, submittedLockTokens)
                        return {
                            status: lockResult.status,
                            headers: lockResult.headers,
                            body: lockResult.body,
                            requestId,
                        }
                    } catch (error) {
                        return {
                            status: error.status || 500,
                            // RFC 4918 condition bodies (no-conflicting-lock,
                            // lock-token-matches-request-uri) ride on the error
                            body: error.body || error.message || 'Internal server error',
                            ...(error.headers ? { headers: error.headers } : {}),
                            requestId,
                        }
                    }

                case 'UNLOCK':
                    try {
                        const unlockResult = await this.UNLOCK(path, headers)
                        return {
                            status: unlockResult.status,
                            headers: unlockResult.headers,
                            body: unlockResult.body,
                            requestId,
                        }
                    } catch (error) {
                        return {
                            status: error.status || 500,
                            body: error.message || 'Internal server error',
                            requestId,
                        }
                    }

                default: {
                    return {
                        status: 405,
                        headers: {
                            Allow: WebDAVFileSystem.IMPLEMENTED_METHODS,
                        },
                        body: `Method ${method} not allowed`,
                        requestId,
                    }
                }
            }
        } catch (error) {
            console.error(`Error in ${method} ${path}:`, error)
            return {
                status: error.status || 500,
                body: error.message || 'Internal server error',
                requestId,
            }
        }
    }
}
